/**
 * Global Audio Context for managing a single shared audio player
 * This ensures only one track plays at a time across all MiniMusicPlayer instances
 */

import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { useLocation } from '@remix-run/react';
import type { MusicTrack } from '~/data/music-tracks';

interface AudioState {
  currentTrackId: string | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isLoading: boolean;
  error: string | null;
  volume: number;
  isMuted: boolean;
}

interface AudioContextValue extends AudioState {
  play: (trackId: string, track: MusicTrack) => void;
  pause: () => void;
  togglePlay: (trackId: string, track: MusicTrack) => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  audioRef: React.RefObject<HTMLAudioElement>;
}

const AudioContext = createContext<AudioContextValue | undefined>(undefined);

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const location = useLocation();

  // Initialize volume from localStorage or use default
  const getInitialVolume = () => {
    if (typeof window === 'undefined') return 0.75;
    const stored = localStorage.getItem('lumora_audio_volume');
    return stored ? parseFloat(stored) : 0.75;
  };

  const getInitialMuted = () => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('lumora_audio_muted');
    return stored === 'true';
  };

  const [state, setState] = useState<AudioState>({
    currentTrackId: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    isLoading: false,
    error: null,
    volume: getInitialVolume(),
    isMuted: getInitialMuted(),
  });

  // Handle play for a specific track
  const play = useCallback((trackId: string, track: MusicTrack) => {
    const audio = audioRef.current;
    if (!audio) return;

    // If switching to a different track, load it
    if (state.currentTrackId !== trackId) {
      setState((prev) => ({
        ...prev,
        currentTrackId: trackId,
        isLoading: true,
        error: null,
        currentTime: 0,
      }));

      audio.src = track.file;
      audio.load();

      // Play once metadata is loaded
      const handleCanPlay = () => {
        audio.play().catch((error) => {
          console.error('Audio playback failed:', error);
          setState((prev) => ({
            ...prev,
            isPlaying: false,
            isLoading: false,
            error: 'Failed to play audio',
          }));
        });
        audio.removeEventListener('canplay', handleCanPlay);
      };

      audio.addEventListener('canplay', handleCanPlay);
    } else {
      // Same track, just resume playing
      audio.play().catch((error) => {
        console.error('Audio playback failed:', error);
        setState((prev) => ({
          ...prev,
          isPlaying: false,
          error: 'Failed to play audio',
        }));
      });
    }

    setState((prev) => ({ ...prev, isPlaying: true }));
  }, [state.currentTrackId]);

  // Handle pause
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    setState((prev) => ({ ...prev, isPlaying: false }));
  }, []);

  // Toggle play/pause for a specific track
  const togglePlay = useCallback(
    (trackId: string, track: MusicTrack) => {
      if (state.isPlaying && state.currentTrackId === trackId) {
        pause();
      } else {
        play(trackId, track);
      }
    },
    [state.isPlaying, state.currentTrackId, play, pause]
  );

  // Handle seek
  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = time;
    setState((prev) => ({ ...prev, currentTime: time }));
  }, []);

  // Handle volume change
  const setVolume = useCallback((volume: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    const clampedVolume = Math.max(0, Math.min(1, volume));
    audio.volume = clampedVolume;

    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('lumora_audio_volume', clampedVolume.toString());
    }

    setState((prev) => ({
      ...prev,
      volume: clampedVolume,
      isMuted: clampedVolume === 0,
    }));
  }, []);

  // Handle mute toggle
  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    setState((prev) => {
      const newMuted = !prev.isMuted;
      audio.muted = newMuted;

      // Persist to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('lumora_audio_muted', newMuted.toString());
      }

      return { ...prev, isMuted: newMuted };
    });
  }, []);

  // Set initial volume on audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = state.volume;
      audio.muted = state.isMuted;
    }
  }, []);

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setState((prev) => ({ ...prev, currentTime: audio.currentTime }));
    };

    const handleLoadedMetadata = () => {
      setState((prev) => ({
        ...prev,
        duration: audio.duration,
        isLoading: false,
      }));
    };

    const handleEnded = () => {
      setState((prev) => ({
        ...prev,
        isPlaying: false,
        currentTime: 0,
      }));
    };

    const handleError = () => {
      setState((prev) => ({
        ...prev,
        isPlaying: false,
        isLoading: false,
        error: 'Failed to load audio',
      }));
    };

    const handlePlay = () => {
      setState((prev) => ({ ...prev, isPlaying: true, isLoading: false }));
    };

    const handlePause = () => {
      setState((prev) => ({ ...prev, isPlaying: false }));
    };

    const handleLoadStart = () => {
      setState((prev) => ({ ...prev, isLoading: true }));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadstart', handleLoadStart);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
  }, []);

  // Pause audio when navigating to a different page
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Pause audio when route changes
    audio.pause();
    setState((prev) => ({ ...prev, isPlaying: false }));
  }, [location.pathname]);

  const value: AudioContextValue = {
    ...state,
    play,
    pause,
    togglePlay,
    seek,
    setVolume,
    toggleMute,
    audioRef,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
      {/* Hidden global audio element */}
      <audio ref={audioRef} preload="metadata" />
    </AudioContext.Provider>
  );
}

/**
 * Hook to access the global audio context
 */
export function useAudio() {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
}
